/* global window */
// ============================================================
// AKSJEEIERBOK OG MØTEDATA
// ============================================================
// Generalforsamlingsprotokollen trenger aksjonaerer og aksjeposter.
// Aksjonaerregisteret har ingen oppslagstjeneste per selskap, saa dette er
// Plummes egen aksjeeierbok. Styremoeteprotokollen trenger hvem som moette, og
// henter forslaget fra styret i `companyRoles`.
//
// Modulen er databasefri. Lagring ligger i `services/backend.jsx`, og reglene
// for hva som staar i protokollen ligger i `functions/annualAccounts/protocolContent.ts`.

const PlummeAnnualMeetings = (() => {
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const cleanText = (value) => String(value == null ? '' : value).trim();
  const num = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const MEETING_KEYS = ['board', 'general'];

  const normalizeShareholder = (entry, index = 0) => {
    const name = cleanText(entry?.name);
    if (!name) return null;
    return {
      id: cleanText(entry?.id) || `shareholder-${index}-${name.toLocaleLowerCase('nb-NO').replace(/[^a-z0-9]+/gi, '-')}`,
      name,
      shares: Math.max(0, Math.round(num(entry?.shares))),
      // Fullmektig er den som moette paa aksjonaerens vegne. Tom naar aksjonaeren moette selv.
      proxy: cleanText(entry?.proxy),
      present: entry?.present !== false,
    };
  };

  const normalizeShareholders = (list) => safeArray(list).map(normalizeShareholder).filter(Boolean);

  const totalShares = (list) => normalizeShareholders(list).reduce((sum, entry) => sum + entry.shares, 0);
  const presentShares = (list) => normalizeShareholders(list)
    .filter((entry) => entry.present)
    .reduce((sum, entry) => sum + entry.shares, 0);

  // Datoen lagres som ISO slik at den sorterer og lar seg sammenligne. Protokollen
  // viser norsk format.
  const toDisplayDate = (isoDate) => {
    const text = cleanText(isoDate);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    return match ? `${match[3]}.${match[2]}.${match[1]}` : text;
  };

  const normalizeMeeting = (meeting) => ({
    date: cleanText(meeting?.date),
    time: cleanText(meeting?.time) || '12:00',
    location: cleanText(meeting?.location),
    // Aksjeloven § 5-16: generalforsamlingsprotokollen signeres av moeteleder og
    // minst én valgt av generalforsamlingen. Uten moeteleder faller
    // signeringslisten tilbake paa styret.
    chairId: cleanText(meeting?.chairId),
    // `null` betyr at brukeren ikke har valgt deltakere ennaa, og da foreslaas
    // hele styret. En tom liste betyr at brukeren aktivt har fjernet alle.
    attendeeIds: Array.isArray(meeting?.attendeeIds) ? meeting.attendeeIds.map(cleanText).filter(Boolean) : null,
    dividend: Math.max(0, num(meeting?.dividend)),
  });

  const normalizeMeetings = (meetings) => MEETING_KEYS.reduce((result, key) => {
    result[key] = normalizeMeeting(meetings?.[key]);
    return result;
  }, {});

  // Styret er forslaget til deltakerliste. Varamedlem og daglig leder uten
  // styreverv er ikke med som standard, men kan legges til av brukeren.
  const suggestedBoardAttendees = (people) => {
    const roles = window.PlummeCompanyRoles;
    if (!roles) return [];
    return roles.signersForDocument(people, 'board');
  };

  const meetingAttendees = (people, meeting) => {
    const roles = window.PlummeCompanyRoles;
    if (!roles) return [];
    const normalized = normalizeMeeting(meeting);
    const everyone = roles.normalizePeople(people);
    if (normalized.attendeeIds === null) return suggestedBoardAttendees(people);
    return everyone.filter((person) => normalized.attendeeIds.includes(person.id));
  };

  const attendeeRows = (people) => {
    const roles = window.PlummeCompanyRoles;
    return safeArray(people).map((person) => ({
      name: person.name,
      roleLabel: roles ? roles.roleLabel(roles.primaryRole(person)) : '',
    }));
  };

  // Aksjeloven § 5-16 krever moeteleder og minst én til, valgt av
  // generalforsamlingen. Er moeteleder valgt, er det den listen som gjelder;
  // ellers faller vi tilbake paa styret slik det var foer feltet fantes.
  const generalMeetingSigners = ({ documentKey, people, meeting }) => {
    if (documentKey !== 'general') return null;
    const roles = window.PlummeCompanyRoles;
    if (!roles) return null;
    const chairId = cleanText(normalizeMeeting(meeting).chairId);
    if (!chairId) return null;
    const everyone = roles.normalizePeople(people);
    const chair = everyone.find((person) => person.id === chairId);
    if (!chair) return null;
    const boardMembers = roles.signersForDocument(people, 'board');
    const coSigner = boardMembers.find((person) => person.id !== chair.id);
    return [chair, coSigner].filter(Boolean);
  };

  // Alt protokollen trenger, samlet paa ett sted. Backend bygger selve teksten.
  const buildProtocolRequest = ({ documentKey, year, company, people, meetings, shareholders, annualResult, createdBy }) => {
    const meeting = normalizeMeeting(meetings?.[documentKey]);
    const attendees = meetingAttendees(people, meeting);
    const roles = window.PlummeCompanyRoles;
    const signers = generalMeetingSigners({ documentKey, people, meeting })
      || (roles ? roles.signersForDocument(people, documentKey) : []);
    return {
      documentKey,
      year,
      company: {
        name: cleanText(company?.name),
        organizationNumber: cleanText(company?.orgnr || company?.organizationNumber),
      },
      meeting: { date: toDisplayDate(meeting.date), time: meeting.time, location: meeting.location },
      dividend: meeting.dividend,
      annualResult: num(annualResult),
      attendees: attendeeRows(attendees),
      boardMemberCount: suggestedBoardAttendees(people).length,
      shareholders: normalizeShareholders(shareholders),
      signers: signers.map((person) => ({
        name: person.name,
        roleLabel: roles ? roles.roleLabel(roles.primaryRole(person)) : '',
        bornAt: toDisplayDate(person.bornAt),
        signedAt: cleanText(person.signedAt),
      })),
      createdBy: { name: cleanText(createdBy?.name), email: cleanText(createdBy?.email) },
    };
  };

  // Protokollen kan ikke lages uten dato, sted og noen til stede. For
  // generalforsamlingen maa aksjeeierboka ogsaa vaere fylt ut.
  const missingForProtocol = ({ documentKey, meetings, people, shareholders }) => {
    const meeting = normalizeMeeting(meetings?.[documentKey]);
    const missing = [];
    if (!meeting.date) missing.push('date');
    if (!meeting.location) missing.push('location');
    if (documentKey === 'board' && !meetingAttendees(people, meeting).length) missing.push('attendees');
    if (documentKey === 'general') {
      const holders = normalizeShareholders(shareholders);
      if (!holders.length) missing.push('shareholders');
      else if (!presentShares(holders)) missing.push('shareholdersPresent');
    }
    return missing;
  };

  return {
    MEETING_KEYS,
    normalizeShareholder,
    normalizeShareholders,
    totalShares,
    presentShares,
    toDisplayDate,
    normalizeMeeting,
    normalizeMeetings,
    suggestedBoardAttendees,
    meetingAttendees,
    attendeeRows,
    buildProtocolRequest,
    missingForProtocol,
    generalMeetingSigners,
  };
})();

Object.assign(window, { PlummeAnnualMeetings });
